import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useProject } from '@/contexts/ProjectContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  BarChart3, Upload, Layers, Eye, GitCompare, Calendar, Activity,
  ScanLine, Database, HardDrive, CheckCircle2, Clock, AlertTriangle,
  Play, XCircle, RefreshCw, FileText, MapPin, ChevronRight,
  Settings2, ArrowRight, Pause, RotateCcw, Download, Filter,
  ChevronDown, Loader2, Box, Crosshair, Ruler, TrendingUp, Smartphone
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, LineChart, Line, AreaChart, Area
} from 'recharts';
import api from '@/lib/api';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import FileUploadZone from '@/components/scan3d/FileUploadZone';
import PointCloudViewer from '@/components/scan3d/PointCloudViewer';
import OctreePointCloudViewer from '@/components/scan3d/OctreePointCloudViewer';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import DesignModelManager from '@/components/scan3d/DesignModelManager';
import ScanModelAlignment from '@/components/scan3d/ScanModelAlignment';
import SideBySideViewer from '@/components/scan3d/SideBySideViewer';
import OverlayDeviationViewer from '@/components/scan3d/OverlayDeviationViewer';
import DeviationTrendChart from '@/components/scan3d/DeviationTrendChart';
import FieldZoneTriage from '@/components/scan3d/FieldZoneTriage';

// ═══════════════════════════════════════════════════════════
// UTILITY HELPERS
// ═══════════════════════════════════════════════════════════
const fmtNum = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} Bn`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} K`;
  return n.toLocaleString('en-IN');
};
const fmtSize = (mb) => {
  if (!mb) return '0 MB';
  if (mb >= 1024 * 1024) return `${(mb / 1024 / 1024).toFixed(2)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '';
const statusColor = (s) => {
  const map = { UPLOADED: '#3b82f6', REGISTERED: '#143A6B', PROCESSING: '#f59e0b', PROCESSED: '#10b981', CONVERTED: '#06b6d4', APPROVED: '#22c55e', QA_REVIEW: '#a855f7', COMPLETED: '#10b981', RUNNING: '#f59e0b', FAILED: '#ef4444', QUEUED: '#6366f1', CANCELLED: '#64748b', CREATED: '#94a3b8', PENDING: '#94a3b8', DATA_CAPTURED: '#3b82f6', ARCHIVED: '#64748b' };
  return map[s] || '#64748b';
};
const methodLabel = (m) => (m || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
const purposeLabel = (p) => (p || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
export default function ScanToModel() {
  const { isDark } = useTheme();
  const { projectId } = useProject();
  const { can: canPerm } = usePermissions();
  const canCreate = canPerm('SCAN3D', 'create');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});
  const [sessionFilter, setSessionFilter] = useState({ area: '', purpose: '', status: '' });
  const [uploadSessionId, setUploadSessionId] = useState(null);
  const [viewerSessionId, setViewerSessionId] = useState('');
  const [viewerFileId, setViewerFileId] = useState('');
  const [viewerPointSize, setViewerPointSize] = useState(3);
  // Octree LOD status for the currently-viewed file
  const [octreeStatus, setOctreeStatus] = useState(null);   // {status, ready, totalPoints, cellCount, error}
  const [octreeBusy, setOctreeBusy] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionForm, setNewSessionForm] = useState({ sessionName: '', sessionDescription: '', scanPurpose: 'PROGRESS_MONITORING', captureMethod: 'MOBILE_LIDAR', equipmentType: '', areas: '', remarks: '' });
  const [creating, setCreating] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [dashRes, sessRes, jobsRes, cmpRes, schedRes, foRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/scan3d/analytics/dashboard`),
        api.get(`/v1/projects/${projectId}/scan3d/sessions?limit=100`),
        api.get(`/v1/projects/${projectId}/scan3d/jobs?limit=100`),
        api.get(`/v1/projects/${projectId}/scan3d/comparisons?limit=100`),
        api.get(`/v1/projects/${projectId}/scan3d/schedule`),
        api.get(`/v1/projects/${projectId}/scan3d/filter-options`),
      ]);
      setDashboard(dashRes.data);
      setSessions(sessRes.data?.sessions || []);
      setJobs(jobsRes.data?.jobs || []);
      setComparisons(cmpRes.data?.comparisons || []);
      setSchedule(schedRes.data);
      setFilterOptions(foRes.data || {});
    } catch (err) { console.error('Scan3D fetch error', err); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // WebSocket for real-time processing job updates
  useEffect(() => {
    if (!projectId) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/scan3d/jobs/ws?projectId=${projectId}`;
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => {};
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'job_started') {
            setJobs(prev => prev.map(j => j.jobId === msg.jobId ? { ...j, execution: { ...j.execution, jobStatus: 'RUNNING', overallProgress: 0 } } : j));
          } else if (msg.type === 'job_progress') {
            setJobs(prev => prev.map(j => j.jobId === msg.jobId ? {
              ...j, execution: { ...j.execution, jobStatus: 'RUNNING', currentStep: msg.step, currentStepProgress: msg.stepProgress, overallProgress: msg.overallProgress }
            } : j));
          } else if (msg.type === 'step_completed') {
            setJobs(prev => prev.map(j => {
              if (j.jobId !== msg.jobId) return j;
              const existing = (j.execution?.stepResults || []).filter(sr => sr.stepName !== msg.step);
              return { ...j, execution: { ...j.execution, stepResults: [...existing, { stepName: msg.step, status: 'COMPLETED', inputPointCount: msg.inputPoints, outputPointCount: msg.outputPoints }] } };
            }));
          } else if (msg.type === 'job_completed') {
            fetchAll();
          }
        } catch (e) { console.error('WS parse error', e); }
      };
    };
    connect();
    return () => { clearTimeout(reconnectTimer); if (ws) { ws.onclose = null; ws.close(); } };
  }, [projectId, fetchAll]);

  // Derive viewer file list from selected session
  const viewerSession = useMemo(() => sessions.find(s => s.sessionId === viewerSessionId), [sessions, viewerSessionId]);
  const viewerFiles = useMemo(() => viewerSession?.rawFiles || [], [viewerSession]);
  const viewerApiUrl = useMemo(() => {
    if (!viewerFileId || !projectId) return null;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const sid = viewerSessionId || '_demo';
    return `${base}/api/v1/projects/${projectId}/scan3d/sessions/${sid}/files/${viewerFileId}/points-bin?limit=6000000`;
  }, [projectId, viewerSessionId, viewerFileId]);

  // Octree status — only meaningful for real (non-demo) files
  const checkOctree = useCallback(async () => {
    if (!viewerFileId || !viewerSessionId || viewerFileId === 'demo' || viewerSessionId === '_demo') {
      setOctreeStatus(null);
      return;
    }
    try {
      const r = await api.get(`/v1/projects/${projectId}/scan3d/sessions/${viewerSessionId}/files/${viewerFileId}/octree/status`);
      setOctreeStatus(r.data);
    } catch (_) {
      setOctreeStatus({ status: 'UNAVAILABLE', ready: false });
    }
  }, [projectId, viewerSessionId, viewerFileId]);

  useEffect(() => { checkOctree(); }, [checkOctree]);

  // Poll while building
  useEffect(() => {
    if (!octreeStatus || octreeStatus.ready || octreeStatus.status !== 'BUILDING') return undefined;
    const id = setInterval(checkOctree, 5000);
    return () => clearInterval(id);
  }, [octreeStatus, checkOctree]);

  const triggerOctreeBuild = useCallback(async (force = false) => {
    if (!viewerFileId || !viewerSessionId) return;
    setOctreeBusy(true);
    try {
      await api.post(`/v1/projects/${projectId}/scan3d/sessions/${viewerSessionId}/files/${viewerFileId}/build-octree${force ? '?force=true' : ''}`);
      checkOctree();
    } catch (e) {
      console.error('Octree build trigger failed', e);
    } finally {
      setOctreeBusy(false);
    }
  }, [projectId, viewerSessionId, viewerFileId, checkOctree]);

  const filteredSessions = useMemo(() => {
    let r = sessions;
    if (sessionFilter.area) r = r.filter(s => s.coverage?.areas?.includes(sessionFilter.area));
    if (sessionFilter.purpose) r = r.filter(s => s.scanPurpose === sessionFilter.purpose);
    if (sessionFilter.status) r = r.filter(s => s.sessionStatus === sessionFilter.status);
    return r;
  }, [sessions, sessionFilter]);

  if (!projectId) return <DashboardLayout><div className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>Select a project to view Scan-to-Model data.</div></DashboardLayout>;

  const cardBg = 'var(--card-bg)';
  const borderColor = 'var(--border-color)';
  const textPrimary = 'var(--text-primary)';
  const textSecondary = 'var(--text-secondary)';
  const inputBg = 'var(--input-bg)';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-5" data-testid="scan-to-model-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: textPrimary }}>
              <ScanLine className="inline w-6 h-6 mr-2 text-cyan-400" />
              Scan-to-Model
            </h1>
            <p className="text-xs mt-0.5" style={{ color: textSecondary }}>Point Cloud & LiDAR Processing Pipeline</p>
          </div>
          <button onClick={fetchAll} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all" style={{ background: inputBg, border: `1px solid ${borderColor}`, color: textSecondary }} data-testid="scan3d-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[9px]" style={{ background: inputBg, border: `1px solid ${borderColor}` }} data-testid="scan3d-ws-indicator">
            <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <span style={{ color: textSecondary }}>{wsConnected ? 'Live' : 'Offline'}</span>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="SCAN_TO_MODEL" defaultOpen={false} compact={true} />
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-transparent border-b rounded-none w-full justify-start gap-0 p-0 h-auto" style={{ borderColor }}>
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'insights', label: 'AI Insights', icon: Activity },
              { id: 'sessions', label: 'Scan Sessions', icon: ScanLine },
              { id: 'designModels', label: 'Design Models', icon: Box },
              { id: 'processing', label: 'Processing', icon: Settings2 },
              { id: 'alignment', label: 'Alignment', icon: Crosshair },
              { id: 'sidebyside', label: 'Side-by-Side', icon: Layers },
              { id: 'overlay', label: 'Overlay & Deviations', icon: Ruler },
              { id: 'trends', label: 'Trends', icon: TrendingUp },
              { id: 'zonesMobile', label: 'Field Triage', icon: Smartphone },
              { id: 'viewer', label: '3D Viewer', icon: Eye },
              { id: 'comparisons', label: 'Comparisons', icon: GitCompare },
              { id: 'schedule', label: 'Scan Schedule', icon: Calendar },
              { id: 'analytics', label: 'Analytics', icon: Activity },
            ].map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.id} value={t.id} data-testid={`scan3d-tab-${t.id}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-400 data-[state=active]:bg-transparent px-3 py-2 text-xs font-medium transition-colors"
                  style={{ color: activeTab === t.id ? 'var(--text-primary)' : textSecondary }}>
                  <Icon className="w-3.5 h-3.5 mr-1.5 inline" />{t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* ═══════ DASHBOARD TAB ═══════ */}
          <TabsContent value="insights" className="space-y-5 mt-4" data-testid="scan3d-insights-tab">
            <ModuleInsightsDashboard projectId={projectId} moduleKey="scanToModel"
              color="#06B6D4" deepLinkBase="/project-controls/scan-to-model" />
          </TabsContent>

          <TabsContent value="dashboard" className="space-y-5 mt-4" data-testid="scan3d-dashboard-tab">
            {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--text-secondary)' }} /></div> : dashboard && (
              <>
                {/* KPI Strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {[
                    { label: 'Total Sessions', value: dashboard.totalSessions, icon: ScanLine, color: '#06b6d4' },
                    { label: 'This Month', value: dashboard.sessionsThisMonth, icon: Calendar, color: '#143A6B' },
                    { label: 'Total Points', value: fmtNum(dashboard.totalPoints), icon: Crosshair, color: '#3b82f6' },
                    { label: 'Processed', value: dashboard.processedModels, icon: CheckCircle2, color: '#10b981' },
                    { label: '3D Viewable', value: dashboard.apsViewable, icon: Eye, color: '#f59e0b' },
                    { label: 'Comparisons', value: dashboard.completedComparisons, icon: GitCompare, color: '#a855f7' },
                    { label: 'Storage', value: fmtSize(dashboard.totalSizeMB), icon: HardDrive, color: '#f97316' },
                  ].map(k => {
                    const Icon = k.icon;
                    return (
                      <Card key={k.label} style={{ background: cardBg, borderColor }} data-testid={`scan3d-kpi-${k.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="p-1.5 rounded-md" style={{ background: `${k.color}12`, border: `1px solid ${k.color}25` }}>
                              <Icon className="w-3.5 h-3.5" style={{ color: k.color }} />
                            </div>
                          </div>
                          <p className="text-lg font-bold font-mono" style={{ color: k.color }}>{k.value}</p>
                          <p className="text-[9px] mt-0.5" style={{ color: textSecondary }}>{k.label}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Status + Monthly Trend */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  <Card style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Session Status</CardTitle></CardHeader>
                    <CardContent>
                      {(() => {
                        const data = Object.entries(dashboard.statusDistribution || {}).map(([k, v]) => ({ name: k.replace(/_/g, ' '), value: v, fill: statusColor(k) }));
                        return data.length ? (
                          <div>
                            <ResponsiveContainer width="100%" height={160}>
                              <PieChart><Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} strokeWidth={0}>{data.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie>
                                <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8, fontSize: 10 }} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-2 mt-2">{data.map(d => <span key={d.name} className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: `${d.fill}15`, color: d.fill }}>{d.name}: {d.value}</span>)}</div>
                          </div>
                        ) : <div className="h-40 flex items-center justify-center text-xs" style={{ color: textSecondary }}>No data</div>;
                      })()}
                    </CardContent>
                  </Card>
                  <Card className="lg:col-span-2" style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Monthly Scan Trend</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={dashboard.monthlyTrend || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                          <XAxis dataKey="month" tick={{ fontSize: 9, fill: textSecondary }} />
                          <YAxis tick={{ fontSize: 9, fill: textSecondary }} />
                          <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8, fontSize: 10 }} />
                          <Area type="monotone" dataKey="count" stroke="#06b6d4" fill="#06b6d420" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Sessions + Active Jobs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Recent Sessions</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {sessions.slice(0, 8).map(s => (
                          <div key={s.sessionId} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors" style={{ background: inputBg, border: `1px solid ${borderColor}` }}
                            onClick={() => { setActiveTab('sessions'); }} data-testid={`scan3d-recent-${s.sessionId}`}>
                            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: statusColor(s.sessionStatus) }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate" style={{ color: textPrimary }}>{s.sessionName}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px]" style={{ color: textSecondary }}>{fmtDate(s.createdAt)}</span>
                                <span className="text-[9px] px-1 py-0 rounded" style={{ background: `${statusColor(s.sessionStatus)}15`, color: statusColor(s.sessionStatus) }}>{s.sessionStatus?.replace(/_/g, ' ')}</span>
                                <span className="text-[9px]" style={{ color: textSecondary }}>{fmtNum(s.totalPointCount)} pts</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Processing Jobs</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {jobs.slice(0, 8).map(j => (
                          <div key={j.jobId} className="p-2.5 rounded-lg" style={{ background: inputBg, border: `1px solid ${borderColor}` }} data-testid={`scan3d-job-${j.jobId}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[10px] font-mono" style={{ color: textSecondary }}>{j.jobId}</span>
                              <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${statusColor(j.execution?.jobStatus)}15`, color: statusColor(j.execution?.jobStatus) }}>{j.execution?.jobStatus}</span>
                            </div>
                            <p className="text-xs truncate mb-1.5" style={{ color: textPrimary }}>{j.jobName}</p>
                            {j.execution?.jobStatus === 'RUNNING' && (
                              <div className="flex items-center gap-2">
                                <Progress value={j.execution.overallProgress} className="h-1.5 flex-1" />
                                <span className="text-[9px] font-mono" style={{ color: '#f59e0b' }}>{j.execution.overallProgress}%</span>
                              </div>
                            )}
                            {j.execution?.jobStatus === 'COMPLETED' && j.execution?.totalProcessingTimeSeconds && (
                              <span className="text-[9px]" style={{ color: textSecondary }}>{Math.round(j.execution.totalProcessingTimeSeconds / 60)} min</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* ═══════ SCAN SESSIONS TAB ═══════ */}
          <TabsContent value="sessions" className="space-y-4 mt-4" data-testid="scan3d-sessions-tab">
            {/* Filters + New Session */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-3.5 h-3.5" style={{ color: textSecondary }} />
              <select value={sessionFilter.area} onChange={e => setSessionFilter(f => ({ ...f, area: e.target.value }))} className="px-2 py-1 text-[10px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-filter-area">
                <option value="">All Areas</option>
                {(filterOptions.areas || []).map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={sessionFilter.purpose} onChange={e => setSessionFilter(f => ({ ...f, purpose: e.target.value }))} className="px-2 py-1 text-[10px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-filter-purpose">
                <option value="">All Purposes</option>
                {(filterOptions.purposes || []).map(p => <option key={p} value={p}>{purposeLabel(p)}</option>)}
              </select>
              <select value={sessionFilter.status} onChange={e => setSessionFilter(f => ({ ...f, status: e.target.value }))} className="px-2 py-1 text-[10px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-filter-status">
                <option value="">All Statuses</option>
                {(filterOptions.statuses || []).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
              {canCreate && <button onClick={() => setShowNewSession(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium bg-cyan-500 hover:bg-cyan-600 text-white transition-colors ml-auto" data-testid="scan3d-new-session-btn">
                <ScanLine className="w-3.5 h-3.5" />New Session
              </button>}
              <span className="text-[10px]" style={{ color: textSecondary }}>{filteredSessions.length} sessions</span>
            </div>

            {/* New Session Modal */}
            {showNewSession && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="scan3d-new-session-modal">
                <div className="w-full max-w-md mx-4 rounded-xl p-5 shadow-2xl" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold" style={{ color: textPrimary }}>
                      <ScanLine className="w-4 h-4 inline mr-2 text-cyan-400" />New Scan Session
                    </h3>
                    <button onClick={() => setShowNewSession(false)} className="p-1 rounded hover:bg-white/10" data-testid="scan3d-new-session-close">
                      <XCircle className="w-4 h-4" style={{ color: textSecondary }} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Session Name *</label>
                      <input value={newSessionForm.sessionName} onChange={e => setNewSessionForm(f => ({ ...f, sessionName: e.target.value }))} placeholder="e.g. Tank Farm — Baseline Survey Apr 2026" className="w-full px-3 py-2 rounded-lg text-xs border outline-none focus:ring-1 focus:ring-cyan-400" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-name" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Description</label>
                      <input value={newSessionForm.sessionDescription} onChange={e => setNewSessionForm(f => ({ ...f, sessionDescription: e.target.value }))} placeholder="Optional description" className="w-full px-3 py-2 rounded-lg text-xs border outline-none focus:ring-1 focus:ring-cyan-400" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-desc" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Scan Purpose</label>
                        <select value={newSessionForm.scanPurpose} onChange={e => setNewSessionForm(f => ({ ...f, scanPurpose: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-xs border outline-none" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-purpose">
                          {['BASELINE_SURVEY', 'PROGRESS_MONITORING', 'CLASH_DETECTION', 'VOLUMETRIC_MEASUREMENT', 'AS_BUILT_DOCUMENTATION', 'QUALITY_INSPECTION'].map(p => <option key={p} value={p}>{purposeLabel(p)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Capture Method</label>
                        <select value={newSessionForm.captureMethod} onChange={e => setNewSessionForm(f => ({ ...f, captureMethod: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-xs border outline-none" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-method">
                          {['MOBILE_LIDAR', 'DRONE_LIDAR', 'TERRESTRIAL_LIDAR', 'DRONE_PHOTOGRAMMETRY', 'HANDHELD_SCANNER'].map(m => <option key={m} value={m}>{methodLabel(m)}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Equipment Type</label>
                        <input value={newSessionForm.equipmentType} onChange={e => setNewSessionForm(f => ({ ...f, equipmentType: e.target.value }))} placeholder="e.g. FARO Focus S350" className="w-full px-3 py-2 rounded-lg text-xs border outline-none focus:ring-1 focus:ring-cyan-400" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-equipment" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Coverage Areas</label>
                        <input value={newSessionForm.areas} onChange={e => setNewSessionForm(f => ({ ...f, areas: e.target.value }))} placeholder="e.g. Tank Farm, Area-2" className="w-full px-3 py-2 rounded-lg text-xs border outline-none focus:ring-1 focus:ring-cyan-400" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-areas" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium mb-1 block" style={{ color: textSecondary }}>Remarks</label>
                      <input value={newSessionForm.remarks} onChange={e => setNewSessionForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional remarks" className="w-full px-3 py-2 rounded-lg text-xs border outline-none focus:ring-1 focus:ring-cyan-400" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-new-session-remarks" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button onClick={() => setShowNewSession(false)} className="px-4 py-2 rounded-lg text-xs font-medium transition-colors" style={{ background: inputBg, border: `1px solid ${borderColor}`, color: textSecondary }} data-testid="scan3d-new-session-cancel">Cancel</button>
                      <button disabled={!newSessionForm.sessionName.trim() || creating} onClick={async () => {
                        setCreating(true);
                        try {
                          const areas = newSessionForm.areas.split(',').map(a => a.trim()).filter(Boolean);
                          await api.post(`/v1/projects/${projectId}/scan3d/sessions`, {
                            sessionName: newSessionForm.sessionName.trim(),
                            sessionDescription: newSessionForm.sessionDescription.trim() || undefined,
                            scanPurpose: newSessionForm.scanPurpose,
                            capture: { captureMethod: newSessionForm.captureMethod, equipmentType: newSessionForm.equipmentType.trim() || undefined, captureDate: new Date().toISOString() },
                            coverage: { areas, zones: [] },
                            remarks: newSessionForm.remarks.trim() || undefined,
                          });
                          setShowNewSession(false);
                          setNewSessionForm({ sessionName: '', sessionDescription: '', scanPurpose: 'PROGRESS_MONITORING', captureMethod: 'MOBILE_LIDAR', equipmentType: '', areas: '', remarks: '' });
                          fetchAll();
                        } catch (err) { console.error('Create session error', err); }
                        setCreating(false);
                      }} className="px-4 py-2 rounded-lg text-xs font-medium bg-cyan-500 hover:bg-cyan-600 text-white transition-colors disabled:opacity-50" data-testid="scan3d-new-session-submit">
                        {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : <ScanLine className="w-3.5 h-3.5 inline mr-1" />}
                        Create Session
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Modal Overlay */}
            {uploadSessionId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="scan3d-upload-modal">
                <div className="w-full max-w-lg mx-4 rounded-xl p-5 shadow-2xl" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold" style={{ color: textPrimary }}>
                      <Upload className="w-4 h-4 inline mr-2 text-cyan-400" />
                      Upload Scan Files
                    </h3>
                    <button onClick={() => setUploadSessionId(null)} className="p-1 rounded hover:bg-white/10" data-testid="scan3d-upload-close">
                      <XCircle className="w-4 h-4" style={{ color: textSecondary }} />
                    </button>
                  </div>
                  <p className="text-[10px] mb-3" style={{ color: textSecondary }}>
                    Session: <span className="font-mono text-cyan-400">{uploadSessionId}</span>
                  </p>
                  <FileUploadZone
                    projectId={projectId}
                    sessionId={uploadSessionId}
                    onUploadComplete={() => { fetchAll(); }}
                  />
                </div>
              </div>
            )}

            {/* Sessions Grid */}
            <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${borderColor}` }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: inputBg }}>
                    {['Code', 'Name', 'Date', 'Area', 'Method', 'Equipment', 'Files', 'Points', 'Size', 'Status', 'Purpose', ''].map(h => (
                      <th key={h || 'actions'} className="px-3 py-2 text-left font-medium" style={{ color: textSecondary, borderBottom: `1px solid ${borderColor}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map(s => (
                    <tr key={s.sessionId} className="hover:opacity-80 transition-opacity" style={{ borderBottom: `1px solid ${borderColor}` }} data-testid={`scan3d-session-row-${s.sessionId}`}>
                      <td className="px-3 py-2 font-mono" style={{ color: '#06b6d4' }}>{s.sessionCode}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate" style={{ color: textPrimary }}>{s.sessionName}</td>
                      <td className="px-3 py-2" style={{ color: textSecondary }}>{fmtDate(s.capture?.captureDate || s.createdAt)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">{(s.coverage?.areas || []).slice(0, 2).map(a => <span key={a} className="text-[8px] px-1 py-0 rounded" style={{ background: `#06b6d415`, color: '#06b6d4' }}>{a}</span>)}</div>
                      </td>
                      <td className="px-3 py-2 text-[10px]" style={{ color: textSecondary }}>{methodLabel(s.capture?.captureMethod)}</td>
                      <td className="px-3 py-2 text-[10px]" style={{ color: textSecondary }}>{(s.capture?.equipmentType || '').replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: textPrimary }}>{s.totalFiles}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: textPrimary }}>{fmtNum(s.totalPointCount)}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: textPrimary }}>{fmtSize(s.totalSizeMB)}</td>
                      <td className="px-3 py-2">
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${statusColor(s.sessionStatus)}15`, color: statusColor(s.sessionStatus) }}>{s.sessionStatus?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-3 py-2 text-[10px]" style={{ color: textSecondary }}>{purposeLabel(s.scanPurpose)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => setUploadSessionId(s.sessionId)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium transition-colors bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400" data-testid={`scan3d-upload-btn-${s.sessionId}`}>
                          <Upload className="w-3 h-3" />Upload
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ═══════ DESIGN MODELS TAB (Phase 1) ═══════ */}
          <TabsContent value="designModels" className="space-y-4 mt-4" data-testid="scan3d-designmodels-tab-content">
            <DesignModelManager projectId={projectId} />
          </TabsContent>

          {/* ═══════ ALIGNMENT TAB (Phase 2) ═══════ */}
          <TabsContent value="alignment" className="space-y-4 mt-4" data-testid="scan3d-alignment-tab-content">
            <ScanModelAlignment projectId={projectId} />
          </TabsContent>

          {/* ═══════ SIDE-BY-SIDE VIEWER (Phase 3) ═══════ */}
          <TabsContent value="sidebyside" className="space-y-4 mt-4" data-testid="scan3d-sidebyside-tab-content">
            <SideBySideViewer projectId={projectId} />
          </TabsContent>

          {/* ═══════ OVERLAY & DEVIATIONS (Phase 4 + 5) ═══════ */}
          <TabsContent value="overlay" className="space-y-4 mt-4" data-testid="scan3d-overlay-tab-content">
            <OverlayDeviationViewer projectId={projectId} />
          </TabsContent>

          {/* ═══════ TRENDS TAB (P2 — deviation time-series) ═══════ */}
          <TabsContent value="trends" className="space-y-4 mt-4" data-testid="scan3d-trends-tab-content">
            <DeviationTrendChart projectId={projectId} />
          </TabsContent>

          {/* ═══════ FIELD ZONE TRIAGE (P2 — mobile-first) ═══════ */}
          <TabsContent value="zonesMobile" className="space-y-4 mt-4" data-testid="scan3d-zones-mobile-tab-content">
            <FieldZoneTriage projectId={projectId} />
          </TabsContent>

          {/* ═══════ PROCESSING TAB ═══════ */}
          <TabsContent value="processing" className="space-y-4 mt-4" data-testid="scan3d-processing-tab">
            {/* Active / Recent Jobs */}
            <div className="space-y-3">
              {jobs.map(j => (
                <Card key={j.jobId} style={{ background: cardBg, borderColor }} data-testid={`scan3d-proc-job-${j.jobId}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: inputBg, color: '#06b6d4' }}>{j.jobCode}</span>
                        <span className="text-sm font-medium truncate max-w-[400px]" style={{ color: textPrimary }}>{j.jobName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] px-2 py-0.5 rounded font-medium" style={{ background: `${statusColor(j.execution?.jobStatus)}15`, color: statusColor(j.execution?.jobStatus) }}>{j.execution?.jobStatus}</span>
                        {j.execution?.jobStatus === 'RUNNING' && (
                          <span className="text-xs font-mono" style={{ color: '#f59e0b' }}>{j.execution.overallProgress}%</span>
                        )}
                      </div>
                    </div>

                    {/* Pipeline Steps Visualization */}
                    <div className="flex items-center gap-1 mb-3 overflow-x-auto">
                      {['registration', 'cleaning', 'downsampling', 'colorization', 'segmentation', 'meshing', 'normal_estimation', 'surface_reconstruction', 'texture_mapping', 'bim_alignment', 'deviation_analysis', 'clash_detection', 'model_export'].map(step => {
                        const stepLabels = { registration: 'Regist', cleaning: 'Clean', downsampling: 'Downsamp', colorization: 'Color', segmentation: 'Segment', meshing: 'Mesh', normal_estimation: 'Normals', surface_reconstruction: 'SurfRecon', texture_mapping: 'Texture', bim_alignment: 'BIM Align', deviation_analysis: 'Deviation', clash_detection: 'Clash', model_export: 'Export' };
                        const pipeConf = j.pipeline?.[step] || {};
                        const enabled = pipeConf.enabled;
                        const stepResult = (j.execution?.stepResults || []).find(sr => sr.stepName === step);
                        const isCurrent = j.execution?.currentStep === step;
                        const sColor = !enabled ? '#374151' : stepResult?.status === 'COMPLETED' ? '#10b981' : stepResult?.status === 'FAILED' ? '#ef4444' : isCurrent ? '#f59e0b' : '#4b5563';
                        return (
                          <div key={step} className="flex items-center gap-1">
                            <div className="px-1.5 py-1 rounded text-[7px] font-medium text-center min-w-[52px] transition-all" style={{ background: `${sColor}18`, border: `1px solid ${sColor}40`, color: sColor }}>
                              {isCurrent && <Loader2 className="w-2.5 h-2.5 animate-spin inline mr-0.5" />}
                              {stepResult?.status === 'COMPLETED' && <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />}
                              {stepResult?.status === 'FAILED' && <XCircle className="w-2.5 h-2.5 inline mr-0.5" />}
                              {!enabled && <span className="opacity-50">OFF</span>}
                              {enabled && (stepLabels[step] || step.charAt(0).toUpperCase() + step.slice(1, 7))}
                            </div>
                            {step !== 'model_export' && <ArrowRight className="w-2.5 h-2.5 shrink-0" style={{ color: borderColor }} />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Progress bar for running */}
                    {j.execution?.jobStatus === 'RUNNING' && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2">
                          <Progress value={j.execution.overallProgress} className="h-2 flex-1" />
                          <span className="text-xs font-mono whitespace-nowrap" style={{ color: textSecondary }}>
                            Step: {j.execution.currentStep} ({j.execution.currentStepProgress}%)
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Step Results */}
                    {(j.execution?.stepResults || []).length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {j.execution.stepResults.map(sr => (
                          <div key={sr.stepName} className="p-2 rounded text-center" style={{ background: inputBg }}>
                            <p className="text-[9px] font-medium capitalize" style={{ color: sr.status === 'COMPLETED' ? '#10b981' : sr.status === 'FAILED' ? '#ef4444' : textSecondary }}>{sr.stepName}</p>
                            {sr.inputPointCount && sr.outputPointCount && (
                              <p className="text-[8px] mt-0.5" style={{ color: textSecondary }}>{fmtNum(sr.inputPointCount)} → {fmtNum(sr.outputPointCount)}</p>
                            )}
                            {sr.durationSeconds && <p className="text-[8px]" style={{ color: textSecondary }}>{sr.durationSeconds}s</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Output Files */}
                    {(j.outputFiles || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                        {j.outputFiles.map(f => (
                          <div key={f.fileId} className="flex items-center gap-1.5 px-2 py-1 rounded text-[9px]" style={{ background: inputBg, border: `1px solid ${borderColor}` }}>
                            <FileText className="w-3 h-3" style={{ color: '#06b6d4' }} />
                            <span style={{ color: textPrimary }}>{f.fileName}</span>
                            <span style={{ color: textSecondary }}>{fmtSize(f.fileSizeMB)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ═══════ 3D VIEWER TAB ═══════ */}
          <TabsContent value="viewer" className="space-y-4 mt-4" data-testid="scan3d-viewer-tab">
            <Card style={{ background: cardBg, borderColor }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2" style={{ color: textPrimary }}>
                  <Eye className="w-4 h-4 text-cyan-400" />3D Point Cloud Viewer
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Toolbar */}
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <select value={viewerSessionId} onChange={e => { setViewerSessionId(e.target.value); setViewerFileId(''); }} className="px-2 py-1 text-[10px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-viewer-session-select">
                    <option value="">Select Scan Session...</option>
                    {sessions.map(s => <option key={s.sessionId} value={s.sessionId}>{s.sessionName} ({s.sessionCode})</option>)}
                  </select>
                  {viewerSessionId && (
                    <select value={viewerFileId} onChange={e => setViewerFileId(e.target.value)} className="px-2 py-1 text-[10px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-viewer-file-select">
                      <option value="">Select File...</option>
                      {viewerFiles.map(f => <option key={f.fileId} value={f.fileId}>{f.fileName} ({fmtSize(f.fileSizeMB)})</option>)}
                      <option value="demo">Sample Point Cloud (Demo)</option>
                    </select>
                  )}
                  {!viewerSessionId && (
                    <button onClick={() => { setViewerSessionId('_demo'); setViewerFileId('demo'); }} className="px-2 py-1 rounded text-[10px] font-medium bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition-colors" data-testid="scan3d-viewer-load-sample">
                      <Eye className="w-3 h-3 inline mr-1" />Load Sample Point Cloud
                    </button>
                  )}
                  <div className="flex items-center gap-1.5 ml-auto">
                    {[
                      { label: 'Side-by-Side', active: true },
                      { label: 'Overlay', active: false },
                      { label: 'Single', active: false },
                    ].map(v => (
                      <button key={v.label} className="px-2 py-1 rounded text-[9px] font-medium" style={{ background: v.active ? '#06b6d420' : inputBg, border: `1px solid ${v.active ? '#06b6d440' : borderColor}`, color: v.active ? '#06b6d4' : textSecondary }}>{v.label}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {[Ruler, Crosshair, MapPin].map((Icon, i) => (
                      <button key={i} className="p-1.5 rounded" style={{ background: inputBg, border: `1px solid ${borderColor}` }}><Icon className="w-3.5 h-3.5" style={{ color: textSecondary }} /></button>
                    ))}
                  </div>
                </div>

                {/* Dual Viewer — Left: Point Cloud (Octree LOD when available, else single-shot), Right: APS Placeholder */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded-lg overflow-hidden relative" style={{ border: `1px solid ${borderColor}`, height: 380 }}>
                    {viewerApiUrl ? (
                      octreeStatus?.ready ? (
                        <OctreePointCloudViewer
                          projectId={projectId}
                          sessionId={viewerSessionId}
                          fileId={viewerFileId}
                          height={380}
                        />
                      ) : (
                        <PointCloudViewer
                          apiUrl={viewerApiUrl}
                          pointSize={viewerPointSize}
                          colorMode="rgb"
                          height={380}
                          isDark={isDark}
                        />
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full" style={{ background: isDark ? '#060a14' : '#f0f4f8' }}>
                        <Box className="w-12 h-12 mb-3" style={{ color: '#06b6d440' }} />
                        <p className="text-sm font-medium" style={{ color: textSecondary }}>Point Cloud Viewer</p>
                        <p className="text-[10px] mt-1" style={{ color: textSecondary }}>Select a session & file to view</p>
                        <p className="text-[9px] mt-3 px-3 py-1 rounded" style={{ background: inputBg, color: textSecondary }}>Three.js WebGL Renderer</p>
                      </div>
                    )}
                    {/* Octree status banner — only when we know the file exists on disk */}
                    {viewerApiUrl && octreeStatus && octreeStatus.status !== 'UNAVAILABLE' && (
                      <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 px-2 py-1 rounded text-[10px]"
                        style={{ background: 'rgba(0,0,0,0.65)', color: '#cbd5e1' }}
                        data-testid="octree-status-bar">
                        {octreeStatus.ready ? (
                          <>
                            <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: '#10b98133', color: '#10b981' }}>LOD STREAMING</span>
                            <span>{octreeStatus.cellCount} cells · {((octreeStatus.totalPoints || 0) / 1e6).toFixed(1)}M points indexed</span>
                            <button onClick={() => triggerOctreeBuild(true)} disabled={octreeBusy}
                              className="ml-auto px-1.5 py-0.5 rounded text-[9px]"
                              style={{ background: '#0f1722', color: '#9ca3af', border: '1px solid #1f2937' }}
                              data-testid="octree-rebuild-btn">
                              {octreeBusy ? 'Rebuilding…' : 'Rebuild'}
                            </button>
                          </>
                        ) : octreeStatus.status === 'BUILDING' ? (
                          <>
                            <span className="px-1.5 py-0.5 rounded font-bold animate-pulse" style={{ background: '#f59e0b33', color: '#f59e0b' }}>BUILDING LOD</span>
                            <span>Octree build in progress — refreshing every 5s</span>
                          </>
                        ) : octreeStatus.status === 'FAILED' ? (
                          <>
                            <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: '#ef444433', color: '#ef4444' }}>LOD BUILD FAILED</span>
                            <span className="opacity-70 truncate">{octreeStatus.error}</span>
                            <button onClick={() => triggerOctreeBuild(true)} disabled={octreeBusy}
                              className="ml-auto px-1.5 py-0.5 rounded text-[9px]"
                              style={{ background: '#0f1722', color: '#9ca3af', border: '1px solid #1f2937' }}>
                              {octreeBusy ? 'Retrying…' : 'Retry'}
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: '#3b82f633', color: '#3b82f6' }}>LOD AVAILABLE</span>
                            <span>Build the octree to stream this scan at up to 500M points</span>
                            <button onClick={() => triggerOctreeBuild(false)} disabled={octreeBusy}
                              className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-semibold"
                              style={{ background: '#06b6d4', color: '#000' }}
                              data-testid="octree-build-btn">
                              {octreeBusy ? 'Triggering…' : 'Build LOD'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg flex flex-col items-center justify-center" style={{ background: isDark ? '#060a14' : '#f0f4f8', border: `1px solid ${borderColor}`, height: 380 }}>
                    <Layers className="w-12 h-12 mb-3" style={{ color: '#3b82f640' }} />
                    <p className="text-sm font-medium" style={{ color: textSecondary }}>Design Model</p>
                    <p className="text-[10px] mt-1" style={{ color: textSecondary }}>3D Model Viewer</p>
                    <p className="text-[9px] mt-3 px-3 py-1 rounded" style={{ background: inputBg, color: textSecondary }}>Native 3D rendering</p>
                  </div>
                </div>

                {/* Viewer Controls */}
                <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px]" style={{ color: textSecondary }}>Point Size:</span>
                    <input type="range" min="1" max="5" step="0.5" value={viewerPointSize} onChange={e => setViewerPointSize(parseFloat(e.target.value))} className="w-20 h-1 accent-cyan-400" data-testid="scan3d-viewer-point-size" />
                    <span className="text-[9px] font-mono w-4" style={{ color: textSecondary }}>{viewerPointSize}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px]" style={{ color: textSecondary }}>Color Mode:</span>
                    <select className="px-1.5 py-0.5 text-[9px] rounded border" style={{ background: inputBg, borderColor, color: textPrimary }} data-testid="scan3d-viewer-color-mode">
                      {['RGB', 'Intensity', 'Elevation', 'Classification', 'Deviation'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px]" style={{ color: textSecondary }}>EDL:</span>
                    <div className="w-8 h-4 rounded-full bg-cyan-500/30 relative cursor-pointer"><div className="w-3 h-3 rounded-full bg-cyan-400 absolute top-0.5 right-0.5" /></div>
                  </div>
                  <span className="text-[9px] ml-auto" style={{ color: textSecondary }}>Camera sync: ON</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══════ COMPARISONS TAB ═══════ */}
          <TabsContent value="comparisons" className="space-y-4 mt-4" data-testid="scan3d-comparisons-tab">
            {comparisons.length === 0 ? (
              <div className="py-16 text-center text-xs" style={{ color: textSecondary }}>No comparisons yet</div>
            ) : (
              <div className="space-y-3">
                {comparisons.map(c => {
                  const dev = c.results?.deviationAnalysis || {};
                  const prog = c.results?.progressDerivation || {};
                  return (
                    <Card key={c.comparisonId} style={{ background: cardBg, borderColor }} data-testid={`scan3d-cmp-${c.comparisonId}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: inputBg, color: '#a855f7' }}>{c.comparisonCode}</span>
                            <span className="text-sm font-medium" style={{ color: textPrimary }}>{c.comparisonName}</span>
                          </div>
                          <span className="text-[9px] px-2 py-0.5 rounded font-medium" style={{ background: `${statusColor(c.results?.status)}15`, color: statusColor(c.results?.status) }}>{c.results?.status}</span>
                        </div>

                        <div className="flex items-center gap-2 mb-3 text-[10px]">
                          <span className="px-2 py-0.5 rounded" style={{ background: '#06b6d415', color: '#06b6d4' }}>{c.sourceA?.label}</span>
                          <GitCompare className="w-3 h-3" style={{ color: textSecondary }} />
                          <span className="px-2 py-0.5 rounded" style={{ background: '#3b82f615', color: '#3b82f6' }}>{c.sourceB?.label}</span>
                          <span className="px-1.5 py-0.5 rounded" style={{ background: inputBg, color: textSecondary }}>{c.comparisonType?.replace(/_/g, ' ')}</span>
                        </div>

                        {c.results?.status === 'COMPLETED' && (
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {[
                              { label: 'Mean Dev', value: `${dev.meanDeviationMM} mm`, color: dev.meanDeviationMM > 25 ? '#ef4444' : dev.meanDeviationMM > 15 ? '#f59e0b' : '#10b981' },
                              { label: 'RMS Dev', value: `${dev.rmsDeviationMM} mm`, color: '#3b82f6' },
                              { label: 'Max Dev', value: `${dev.maxDeviationMM} mm`, color: '#ef4444' },
                              { label: 'Within Tol', value: `${dev.withinTolerancePercent}%`, color: dev.withinTolerancePercent >= 90 ? '#10b981' : '#f59e0b' },
                              { label: 'Matched', value: `${prog.matchedObjects || 0}/${prog.totalDesignObjects || 0}`, color: '#143A6B' },
                              { label: 'Progress', value: `${prog.derivedProgressPercent || 0}%`, color: '#06b6d4' },
                            ].map(m => (
                              <div key={m.label} className="p-2 rounded text-center" style={{ background: inputBg }}>
                                <p className="text-sm font-bold font-mono" style={{ color: m.color }}>{m.value}</p>
                                <p className="text-[8px] mt-0.5" style={{ color: textSecondary }}>{m.label}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Deviation Scale */}
                        {c.results?.status === 'COMPLETED' && dev.toleranceLimitMM && (
                          <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                            <span className="text-[9px]" style={{ color: textSecondary }}>Deviation Scale:</span>
                            <div className="flex-1 h-2 rounded-full" style={{ background: 'linear-gradient(to right, #3b82f6, #10b981, #f59e0b, #ef4444)' }} />
                            <div className="flex gap-3 text-[8px]" style={{ color: textSecondary }}>
                              <span>0mm</span><span>{dev.toleranceLimitMM / 2}mm</span><span>{dev.toleranceLimitMM}mm</span><span>{'>'}{dev.toleranceLimitMM * 2}mm</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ═══════ SCAN SCHEDULE TAB ═══════ */}
          <TabsContent value="schedule" className="space-y-4 mt-4" data-testid="scan3d-schedule-tab">
            <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${borderColor}` }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: inputBg }}>
                    {['Area', 'Scan Type', 'Frequency', 'Purpose', 'Next Scan', 'Last Scan', 'Surveyor', 'Company', 'Status'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: textSecondary, borderBottom: `1px solid ${borderColor}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(schedule?.scanPlan || []).map(p => {
                    const nextDate = p.nextScanDate ? new Date(p.nextScanDate) : null;
                    const isOverdue = nextDate && nextDate < new Date();
                    const isUpcoming = nextDate && !isOverdue && (nextDate - new Date()) < 7 * 24 * 60 * 60 * 1000;
                    return (
                      <tr key={p.planId} style={{ borderBottom: `1px solid ${borderColor}` }} data-testid={`scan3d-sched-${p.planId}`}>
                        <td className="px-3 py-2 font-medium" style={{ color: textPrimary }}>{p.areaName}</td>
                        <td className="px-3 py-2" style={{ color: textSecondary }}>{methodLabel(p.scanType)}</td>
                        <td className="px-3 py-2"><span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: '#143A6B15', color: '#143A6B' }}>{p.frequency}</span></td>
                        <td className="px-3 py-2" style={{ color: textSecondary }}>{purposeLabel(p.purpose)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-medium ${isOverdue ? 'text-red-400' : isUpcoming ? 'text-amber-400' : ''}`} style={!isOverdue && !isUpcoming ? { color: textPrimary } : {}}>
                            {fmtDate(p.nextScanDate)}
                            {isOverdue && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" />}
                          </span>
                        </td>
                        <td className="px-3 py-2" style={{ color: textSecondary }}>{fmtDate(p.lastScanDate)}</td>
                        <td className="px-3 py-2" style={{ color: textPrimary }}>{p.assignedSurveyor}</td>
                        <td className="px-3 py-2" style={{ color: textSecondary }}>{p.assignedCompany}</td>
                        <td className="px-3 py-2"><span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: p.isActive ? '#10b98115' : '#ef444415', color: p.isActive ? '#10b981' : '#ef4444' }}>{p.isActive ? 'ACTIVE' : 'INACTIVE'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ═══════ ANALYTICS TAB ═══════ */}
          <TabsContent value="analytics" className="space-y-5 mt-4" data-testid="scan3d-analytics-tab">
            {dashboard && (
              <>
                {/* Coverage by Area */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Coverage by Area</CardTitle></CardHeader>
                    <CardContent>
                      {(() => {
                        const areaData = {};
                        sessions.forEach(s => { (s.coverage?.areas || []).forEach(a => { areaData[a] = (areaData[a] || 0) + 1; }); });
                        const data = Object.entries(areaData).map(([k, v]) => ({ area: k, scans: v }));
                        return (
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={data} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                              <XAxis type="number" tick={{ fontSize: 9, fill: textSecondary }} />
                              <YAxis type="category" dataKey="area" tick={{ fontSize: 9, fill: textSecondary }} width={80} />
                              <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8, fontSize: 10 }} />
                              <Bar dataKey="scans" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <Card style={{ background: cardBg, borderColor }}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Capture Methods</CardTitle></CardHeader>
                    <CardContent>
                      {(() => {
                        const mData = {};
                        sessions.forEach(s => { const m = s.capture?.captureMethod; if (m) mData[m] = (mData[m] || 0) + 1; });
                        const colors = ['#06b6d4', '#3b82f6', '#143A6B', '#f59e0b', '#10b981'];
                        const data = Object.entries(mData).map(([k, v], i) => ({ name: methodLabel(k), value: v, fill: colors[i % colors.length] }));
                        return (
                          <div>
                            <ResponsiveContainer width="100%" height={160}>
                              <PieChart><Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={38} outerRadius={60} paddingAngle={3} strokeWidth={0}>{data.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie>
                                <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8, fontSize: 10 }} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-2 mt-2 justify-center">{data.map(d => <span key={d.name} className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: `${d.fill}15`, color: d.fill }}>{d.name}: {d.value}</span>)}</div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Comparison Deviations */}
                <Card style={{ background: cardBg, borderColor }}>
                  <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ color: textPrimary }}>Comparison Deviation Trends</CardTitle></CardHeader>
                  <CardContent>
                    {(() => {
                      const devData = comparisons.filter(c => c.results?.status === 'COMPLETED' && c.results?.deviationAnalysis).map(c => ({
                        name: c.comparisonCode,
                        mean: c.results.deviationAnalysis.meanDeviationMM,
                        rms: c.results.deviationAnalysis.rmsDeviationMM,
                        within: c.results.deviationAnalysis.withinTolerancePercent,
                      }));
                      return (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={devData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                            <XAxis dataKey="name" tick={{ fontSize: 8, fill: textSecondary }} />
                            <YAxis tick={{ fontSize: 9, fill: textSecondary }} />
                            <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8, fontSize: 10 }} />
                            <Line type="monotone" dataKey="mean" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Mean (mm)" />
                            <Line type="monotone" dataKey="rms" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="RMS (mm)" />
                          </LineChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
